// =======================================================
// File: src/components/Timers/TimeLogs.jsx
// Drop-in
// Upgrades:
// - Clearer filters (Reset button + "This week / Last 7d" quick set)
// - Robust CSV export (UTC ISO + safe escaping) and JSON export
// - Totals bar shows HH:MM as well as minutes
// - Safer guards for missing fields; stable memoization
// - Minor a11y, empty-state polish
// =======================================================
import React, { useMemo, useState } from "react";
import { useVoiceTimer } from "../../contexts/VoiceTimerContext";
import { getAllStaff } from "../../utils/staff";
import TimeLogsCharts from "./TimeLogsCharts";

const STEP_LABEL = {
  throw: "Throwing",
  trim: "Trimming",
  attach_handle: "Attach Handle",
  glaze: "Glazing",
};
const stepLabel = (k) => STEP_LABEL[k] || (k?.charAt(0).toUpperCase() + k?.slice(1));

const toDateInputValue = (ms) => {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fromDateInputValue = (val, endOfDay = false) => {
  if (!val) return null;
  const [y, m, d] = val.split("-").map(Number);
  return new Date(
    y,
    m - 1,
    d,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0
  ).getTime();
};

function mmToHHMM(totalMin) {
  const m = Math.max(0, Math.round(Number(totalMin) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${String(h).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function TimeLogs() {
  const { logs = [], clearLogs } = useVoiceTimer();
  const staff = getAllStaff() || [];

  const [start, setStart] = useState(() => toDateInputValue(Date.now() - 7 * 86400000));
  const [end, setEnd] = useState(() => toDateInputValue(Date.now()));
  const [batch, setBatch] = useState("");
  const [step, setStep] = useState("");
  const [ptype, setPtype] = useState("");
  const [staffId, setStaffId] = useState("");

  const filtered = useMemo(() => {
    const s = start ? fromDateInputValue(start) : null;
    const e = end ? fromDateInputValue(end, true) : null;
    return (logs || []).filter((L) => {
      const st = L.startedAt ?? 0;
      const ft = L.finishedAt ?? st;
      if (s && st < s) return false;
      if (e && ft > e) return false;
      if (batch && String(L.batchId ?? "") !== String(batch)) return false;
      if (step && (L.step ?? "") !== step) return false;
      if (ptype && (L.productType ?? "") !== ptype) return false;
      if (staffId && String(L.staffId ?? "") !== String(staffId)) return false;
      return true;
    });
  }, [logs, start, end, batch, step, ptype, staffId]);

  const totals = useMemo(() => {
    const minutes = filtered.reduce((a, L) => a + (Number(L.minutes) || 0), 0);
    const sessions = filtered.length;
    const batches = new Set(filtered.map((L) => L.batchId)).size;

    const byStep = Object.values(
      filtered.reduce((acc, L) => {
        const k = L.step || "unknown";
        acc[k] = acc[k] || { step: k, minutes: 0, sessions: 0 };
        acc[k].minutes += Number(L.minutes) || 0;
        acc[k].sessions += 1;
        return acc;
      }, {})
    ).sort((a, b) => b.minutes - a.minutes);

    const byStaff = Object.values(
      filtered.reduce((acc, L) => {
        const k = String(L.staffId || "unassigned");
        acc[k] = acc[k] || { staffId: k, minutes: 0, sessions: 0 };
        acc[k].minutes += Number(L.minutes) || 0;
        acc[k].sessions += 1;
        return acc;
      }, {})
    ).sort((a, b) => b.minutes - a.minutes);

    return { minutes, sessions, batches, byStep, byStaff };
  }, [filtered]);

  function exportCSV() {
    const headers = [
      "id",
      "step",
      "batchId",
      "productType",
      "productId",
      "staffId",
      "startedAtISO",
      "finishedAtISO",
      "minutes",
    ];
    const rows = filtered.map((L) => [
      L.id ?? "",
      L.step ?? "",
      L.batchId ?? "",
      L.productType ?? "",
      L.productId ?? "",
      L.staffId ?? "",
      new Date(L.startedAt ?? 0).toISOString(),
      new Date((L.finishedAt ?? L.startedAt) ?? 0).toISOString(),
      (Number(L.minutes) || 0).toFixed(1),
    ]);
    const csvEscape = (val) => {
      const s = String(val ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mudo_time_logs_${toDateInputValue(Date.now())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function exportJSON() {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mudo_time_logs_${toDateInputValue(Date.now())}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const stepsOpt = useMemo(
    () => Array.from(new Set((logs || []).map((l) => l.step))).filter(Boolean),
    [logs]
  );
  const typesOpt = useMemo(
    () => Array.from(new Set((logs || []).map((l) => l.productType || ""))),
    [logs]
  );
  const staffOpt = useMemo(() => {
    if (staff.length) return staff;
    const ids = Array.from(new Set((logs || []).map((l) => l.staffId))).filter(Boolean);
    return ids.map((id) => ({ id, name: String(id) }));
  }, [staff, logs]);

  const resetFilters = () => {
    setStart(toDateInputValue(Date.now() - 7 * 86400000));
    setEnd(toDateInputValue(Date.now()));
    setBatch("");
    setStep("");
    setPtype("");
    setStaffId("");
  };

  const setThisWeek = () => {
    const now = new Date();
    const day = now.getDay(); // 0..6 (Sun..Sat)
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    setStart(toDateInputValue(monday.getTime()));
    setEnd(toDateInputValue(Date.now()));
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Time Logs</h1>
        <div className="flex gap-2">
          <button className="px-3 py-2 rounded-xl border" onClick={setThisWeek} title="Set to this week">
            This week
          </button>
          <button
            className="px-3 py-2 rounded-xl border"
            onClick={() => {
              setStart(toDateInputValue(Date.now() - 7 * 86400000));
              setEnd(toDateInputValue(Date.now()));
            }}
            title="Set to last 7 days"
          >
            Last 7d
          </button>
          <button className="px-3 py-2 rounded-xl border" onClick={resetFilters} title="Reset all filters">
            Reset
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-6 gap-3 items-end">
        <Field label="Start">
          <input
            type="date"
            className="w-full border rounded-xl px-3 py-2"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </Field>
        <Field label="End">
          <input
            type="date"
            className="w-full border rounded-xl px-3 py-2"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </Field>
        <Field label="Batch">
          <input
            type="text"
            placeholder="e.g. 14"
            className="w-full border rounded-xl px-3 py-2"
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
          />
        </Field>
        <Field label="Step">
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={step}
            onChange={(e) => setStep(e.target.value)}
          >
            <option value="">All</option>
            {stepsOpt.map((s) => (
              <option key={s} value={s}>
                {stepLabel(s)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Product type">
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={ptype}
            onChange={(e) => setPtype(e.target.value)}
          >
            <option value="">All</option>
            {typesOpt.map((t) => (
              <option key={t} value={t}>
                {t || "(Unspecified)"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Staff">
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
          >
            <option value="">All</option>
            {staffOpt.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Staff ${s.id}`}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Card title="Total minutes" value={`${totals.minutes.toFixed(1)} min (${mmToHHMM(totals.minutes)})`} />
        <Card title="Sessions" value={String(totals.sessions)} />
        <Card title="Batches" value={String(totals.batches)} />
      </div>

      <TimeLogsCharts logs={filtered} staffList={staffOpt} stepLabel={stepLabel} />

      <div className="rounded-2xl border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <Th>Date</Th>
              <Th>Batch</Th>
              <Th>Step</Th>
              <Th>Product type</Th>
              <Th>Staff</Th>
              <Th className="text-right">Minutes</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-3 text-center opacity-70">
                  No logs match your filters.
                </td>
              </tr>
            )}
            {filtered.map((L) => {
              const when = new Date((L.finishedAt ?? L.startedAt) ?? 0);
              const staffName =
                staffOpt.find((s) => String(s.id) === String(L.staffId))?.name || "—";
              return (
                <tr key={L.id} className="border-t">
                  <Td>{when.toLocaleString()}</Td>
                  <Td>{L.batchId != null ? `#${L.batchId}` : "—"}</Td>
                  <Td>{stepLabel(L.step)}</Td>
                  <Td>{L.productType || "—"}</Td>
                  <Td>{staffName}</Td>
                  <Td className="text-right">{Number(L.minutes || 0).toFixed(1)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          <button className="px-3 py-2 rounded-xl border" onClick={exportCSV} title="Download CSV">
            Export CSV
          </button>
          <button className="px-3 py-2 rounded-xl border" onClick={exportJSON} title="Download JSON">
            Export JSON
          </button>
        </div>
        <button
          className="px-3 py-2 rounded-xl border"
          onClick={() => {
            if (confirm("Delete ALL time logs?")) clearLogs();
          }}
          title="Clear all time logs on this device"
        >
          Clear all
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm opacity-70 mb-1">{label}</label>
      {children}
    </div>
  );
}
function Card({ title, value }) {
  return (
    <div className="rounded-2xl border p-3">
      <div className="text-sm opacity-70">{title}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={`text-left font-medium px-3 py-2 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}
